import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Fab,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { BriefFieldDefinition, BriefData } from '../types/briefTypes';
import styled from 'styled-components';
import { saveTemplate } from '../services/firebase';

// Add dummy briefData for key validation
const dummyBriefData: BriefData = {
  campaignName: '',
  campaignType: '',
  primaryObjective: '',
  eventObjective: '',
  eventDate: '',
  targetAudience: '',
  budget: '',
  timeline: '',
  keyMessages: '',
  channels: '',
  activityKPIs: '',
  businessKPIs: '',
  creativeAssets: '',
  additionalNotes: ''
};

const EditorContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
`;

const EditorSection = styled(Box)`
  display: flex;
  gap: 2rem;
`;

const TemplatePanel = styled(Paper)`
  flex: 2;
  padding: 1rem;
  overflow-y: auto;
  max-height: calc(100vh - 400px);
`;

const SidePanel = styled(Paper)`
  flex: 1;
  padding: 1rem;
  height: fit-content;
`;

const PromptsPanel = styled(Paper)`
  padding: 1rem;
  margin-bottom: 2rem;
`;

const DraggableItem = styled(Card)<{ isDragging: boolean }>`
  margin: 0.5rem 0;
  background-color: ${props => props.isDragging ? '#f0f7ff' : 'white'};
  border: 1px solid ${props => props.isDragging ? '#2196f3' : '#e0e0e0'};
`;

const FieldRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
`;

const StyledButton = styled(Button)`
  background-color: #e0301e !important;
  color: #ffffff !important;
  &:hover {
    background-color: #e84835 !important;
  }
  &:active {
    background-color: #c41c0b !important;
  }
`;

const SaveFab = styled(Fab)`
  background-color: #e0301e !important;
  color: #ffffff !important;
  &:hover {
    background-color: #e84835 !important;
  }
  &:active {
    background-color: #c41c0b !important;
  }
`;

interface FieldEditorProps {
  field: BriefFieldDefinition;
  onUpdate: (field: BriefFieldDefinition) => void;
  onDelete: () => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  onUpdate,
  onDelete,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleUpdate = (updates: Partial<BriefFieldDefinition>) => {
    onUpdate({ ...field, ...updates });
  };

  return (
    <>
      <FieldRow>
        <DragIcon />
        <Typography>{field.label}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={() => setIsDialogOpen(true)} size="small">
          <SettingsIcon />
        </IconButton>
        <IconButton onClick={onDelete} size="small">
          <DeleteIcon />
        </IconButton>
      </FieldRow>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Field</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Field Label"
              value={field.label}
              onChange={(e) => handleUpdate({ label: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Field Key</InputLabel>
              <Select
                value={field.key}
                onChange={(e) => {
                  const value = e.target.value;
                  if (typeof value === 'string' && Object.keys(dummyBriefData).includes(value)) {
                    handleUpdate({ key: value as keyof BriefData });
                  }
                }}
                label="Field Key"
              >
                <MenuItem value="campaignName">Campaign Name</MenuItem>
                <MenuItem value="campaignType">Campaign Type</MenuItem>
                <MenuItem value="primaryObjective">Primary Objective</MenuItem>
                <MenuItem value="eventObjective">Event Objective</MenuItem>
                <MenuItem value="eventDate">Event Date</MenuItem>
                <MenuItem value="targetAudience">Target Audience</MenuItem>
                <MenuItem value="budget">Budget</MenuItem>
                <MenuItem value="timeline">Timeline</MenuItem>
                <MenuItem value="keyMessages">Key Messages</MenuItem>
                <MenuItem value="channels">Channels</MenuItem>
                <MenuItem value="activityKPIs">Activity KPIs</MenuItem>
                <MenuItem value="businessKPIs">Business KPIs</MenuItem>
                <MenuItem value="creativeAssets">Creative Assets</MenuItem>
                <MenuItem value="additionalNotes">Additional Notes</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={field.type}
                onChange={(e) => handleUpdate({ type: e.target.value as BriefFieldDefinition['type'] })}
                label="Field Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="picklist">Picklist</MenuItem>
                <MenuItem value="multiline">Multiline</MenuItem>
                <MenuItem value="date">Date</MenuItem>
              </Select>
            </FormControl>
            
            {field.type === 'picklist' && (
              <TextField
                label="Options (comma-separated)"
                value={field.options?.join(', ') || ''}
                onChange={(e) => handleUpdate({ options: e.target.value.split(',').map(s => s.trim()) })}
                fullWidth
                multiline
              />
            )}

            <TextField
              label="Explanation"
              value={field.explanation}
              onChange={(e) => handleUpdate({ explanation: e.target.value })}
              fullWidth
              multiline
            />

            <FormControlLabel
              control={
                <Switch
                  checked={field.required}
                  onChange={(e) => handleUpdate({ required: e.target.checked })}
                />
              }
              label="Required Field"
            />

            {/* AI Extraction Rules */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>AI Extraction Rules</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Extraction Type</InputLabel>
                  <Select
                    value={field.aiExtract?.type || 'none'}
                    onChange={(e) => handleUpdate({
                      aiExtract: {
                        type: e.target.value as 'goal' | 'event_type' | 'date' | 'none',
                        relatedFields: field.aiExtract?.relatedFields
                      }
                    })}
                    label="Extraction Type"
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="goal">Goal</MenuItem>
                    <MenuItem value="event_type">Event Type</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface AIPromptConfig {
  targetAudience: string;
  informationExtraction: string;
  recommendations: string;
}

interface PrerequisiteItem {
  id: string;
  text: string;
  required: boolean;
}

interface CampaignPrerequisites {
  campaignType: string;
  items: PrerequisiteItem[];
}

interface ConversationConfig {
  initialGreeting: string;
  fieldPrompts: Record<string, string>;
  followUpStyle: string;
  responseStyle: string;
  prerequisites: CampaignPrerequisites[];
}

interface TemplateEditorProps {
  fields: BriefFieldDefinition[];
  onFieldsChange: (fields: BriefFieldDefinition[]) => void;
  aiPrompts?: AIPromptConfig;
  onAIPromptsChange?: (prompts: AIPromptConfig) => void;
  conversationConfig?: ConversationConfig;
  onConversationConfigChange?: (config: ConversationConfig) => void;
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  fields,
  onFieldsChange,
  aiPrompts,
  onAIPromptsChange,
  conversationConfig = {
    initialGreeting: `Cześć! Jestem asystentem AI i pomogę Ci stworzyć brief kampanii marketingowej. Przeprowadzę Cię przez cały proces. Zacznijmy od omówienia Twojej kampanii - opowiedz mi o niej?`,
    fieldPrompts: {},
    followUpStyle: `When asking follow-up questions:
- Be concise and clear
- One question at a time
- Acknowledge previous information
- Use context from earlier responses
- Maintain a conversational tone`,
    responseStyle: `When responding to user input:
- Be professional yet friendly
- Acknowledge user's input
- Provide relevant suggestions
- Stay focused on the campaign brief
- Guide user through the process`,
    prerequisites: []
  },
  onConversationConfigChange
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      // For now, we'll use a hardcoded user ID. In a real app, this would come from authentication
      const userId = 'default-user';
      const templateData = {
        fields,
        aiPrompts: aiPrompts || {
          targetAudience: '',
          informationExtraction: '',
          recommendations: ''
        },
        conversationConfig
      };

      const success = await saveTemplate(userId, templateData);
      
      if (success) {
        setShowSaveSuccess(true);
      } else {
        setSaveError('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setSaveError('An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  }, [fields, aiPrompts, conversationConfig]);

  // Auto-save functionality
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 3000); // Auto-save after 3 seconds of no changes

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [fields, aiPrompts, conversationConfig, handleSave]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onFieldsChange(items);
  };

  const addField = () => {
    const newField: BriefFieldDefinition = {
      key: 'campaignName', // Default to a valid key
      label: 'New Field',
      explanation: 'Field explanation',
      type: 'text',
      required: false,
      aiExtract: { type: 'none' }
    };

    onFieldsChange([...fields, newField]);
  };

  const updateField = (index: number, updates: Partial<BriefFieldDefinition>) => {
    const updatedFields = fields.map((field, i) =>
      i === index ? { ...field, ...updates } : field
    );
    onFieldsChange(updatedFields);
  };

  const deleteField = (index: number) => {
    const updatedFields = fields.filter((_, i) => i !== index);
    onFieldsChange(updatedFields);
  };

  const PrerequisitesSection = () => {
    const [newPrerequisite, setNewPrerequisite] = useState({
      campaignType: '',
      prerequisiteText: '',
      required: true
    });

    const handleAddPrerequisite = () => {
      if (!newPrerequisite.campaignType || !newPrerequisite.prerequisiteText) return;

      const updatedConfig = { ...conversationConfig };
      const existingTypeIndex = updatedConfig.prerequisites.findIndex(
        p => p.campaignType === newPrerequisite.campaignType
      );

      if (existingTypeIndex >= 0) {
        updatedConfig.prerequisites[existingTypeIndex].items.push({
          id: Math.random().toString(36).substr(2, 9),
          text: newPrerequisite.prerequisiteText,
          required: newPrerequisite.required
        });
      } else {
        updatedConfig.prerequisites.push({
          campaignType: newPrerequisite.campaignType,
          items: [{
            id: Math.random().toString(36).substr(2, 9),
            text: newPrerequisite.prerequisiteText,
            required: newPrerequisite.required
          }]
        });
      }

      onConversationConfigChange?.(updatedConfig);
      setNewPrerequisite({ campaignType: '', prerequisiteText: '', required: true });
    };

    const handleDeletePrerequisite = (campaignType: string, itemId: string) => {
      const updatedConfig = { ...conversationConfig };
      const typeIndex = updatedConfig.prerequisites.findIndex(p => p.campaignType === campaignType);
      
      if (typeIndex >= 0) {
        updatedConfig.prerequisites[typeIndex].items = 
          updatedConfig.prerequisites[typeIndex].items.filter(item => item.id !== itemId);
        
        if (updatedConfig.prerequisites[typeIndex].items.length === 0) {
          updatedConfig.prerequisites.splice(typeIndex, 1);
        }
        
        onConversationConfigChange?.(updatedConfig);
      }
    };

    return (
      <PromptsPanel elevation={1}>
        <Typography variant="h5" gutterBottom>Prerequisites</Typography>
        <Typography color="textSecondary" gutterBottom sx={{ mb: 2 }}>
          Define prerequisites that need to be checked before starting specific campaign types.
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Campaign Type</InputLabel>
            <Select
              value={newPrerequisite.campaignType}
              onChange={(e) => setNewPrerequisite({
                ...newPrerequisite,
                campaignType: e.target.value
              })}
              label="Campaign Type"
            >
              <MenuItem value="webinar">Webinar</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="article">Article</MenuItem>
              <MenuItem value="social">Social Media</MenuItem>
              <MenuItem value="email">Email Campaign</MenuItem>
            </Select>
          </FormControl>

          <TextField
            sx={{ flex: 1 }}
            label="Prerequisite"
            value={newPrerequisite.prerequisiteText}
            onChange={(e) => setNewPrerequisite({
              ...newPrerequisite,
              prerequisiteText: e.target.value
            })}
            placeholder="e.g., Does the business have a product page?"
          />

          <FormControlLabel
            control={
              <Switch
                checked={newPrerequisite.required}
                onChange={(e) => setNewPrerequisite({
                  ...newPrerequisite,
                  required: e.target.checked
                })}
              />
            }
            label="Required"
          />

          <StyledButton
            variant="contained"
            onClick={handleAddPrerequisite}
            disabled={!newPrerequisite.campaignType || !newPrerequisite.prerequisiteText}
          >
            Add
          </StyledButton>
        </Box>

        {conversationConfig.prerequisites.map((prerequisite) => (
          <Accordion key={prerequisite.campaignType}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                {prerequisite.campaignType} Prerequisites
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {prerequisite.items.map((item) => (
                  <ListItem
                    key={item.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleDeletePrerequisite(prerequisite.campaignType, item.id)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={item.text}
                      secondary={item.required ? 'Required' : 'Optional'}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </PromptsPanel>
    );
  };

  return (
    <EditorContainer>
      {/* AI Prompts Section */}
      <PromptsPanel elevation={1}>
        <Typography variant="h5" gutterBottom>AI Prompts Configuration</Typography>
        <Typography color="textSecondary" gutterBottom sx={{ mb: 2 }}>
          Customize the prompts used by AI to generate content and extract information.
        </Typography>
        
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Target Audience Generation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={10}
              fullWidth
              value={aiPrompts?.targetAudience ?? ''}
              onChange={(e) => onAIPromptsChange?.({
                ...(aiPrompts ?? {}),
                targetAudience: e.target.value
              } as AIPromptConfig)}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Information Extraction</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={10}
              fullWidth
              value={aiPrompts?.informationExtraction ?? ''}
              onChange={(e) => onAIPromptsChange?.({
                ...(aiPrompts ?? {}),
                informationExtraction: e.target.value
              } as AIPromptConfig)}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Recommendations Generation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={10}
              fullWidth
              value={aiPrompts?.recommendations ?? ''}
              onChange={(e) => onAIPromptsChange?.({
                ...(aiPrompts ?? {}),
                recommendations: e.target.value
              } as AIPromptConfig)}
            />
          </AccordionDetails>
        </Accordion>
      </PromptsPanel>

      {/* Prerequisites Section */}
      <PrerequisitesSection />

      {/* Conversation Flow Section */}
      <PromptsPanel elevation={1}>
        <Typography variant="h5" gutterBottom>Conversation Flow</Typography>
        <Typography color="textSecondary" gutterBottom sx={{ mb: 2 }}>
          Customize how the AI agent interacts with users during the brief creation process.
        </Typography>
        
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Initial Greeting</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={4}
              fullWidth
              value={conversationConfig.initialGreeting}
              onChange={(e) => onConversationConfigChange?.({
                ...conversationConfig,
                initialGreeting: e.target.value
              })}
              placeholder="Enter the initial greeting message..."
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Field-Specific Prompts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {fields.map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  multiline
                  rows={2}
                  fullWidth
                  value={conversationConfig.fieldPrompts[field.key] || ''}
                  onChange={(e) => onConversationConfigChange?.({
                    ...conversationConfig,
                    fieldPrompts: {
                      ...conversationConfig.fieldPrompts,
                      [field.key]: e.target.value
                    }
                  })}
                  placeholder={`Enter prompt for ${field.label}...`}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Follow-up Question Style</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={conversationConfig.followUpStyle}
              onChange={(e) => onConversationConfigChange?.({
                ...conversationConfig,
                followUpStyle: e.target.value
              })}
              placeholder="Enter guidelines for follow-up questions..."
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Response Style</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={conversationConfig.responseStyle}
              onChange={(e) => onConversationConfigChange?.({
                ...conversationConfig,
                responseStyle: e.target.value
              })}
              placeholder="Enter guidelines for response style..."
            />
          </AccordionDetails>
        </Accordion>
      </PromptsPanel>

      {/* Template Fields Section */}
      <EditorSection>
        <TemplatePanel elevation={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>Brief Template Structure</Typography>
            <Typography color="textSecondary" gutterBottom>
              Customize the fields in your marketing brief template. Drag to reorder fields.
            </Typography>
          </Box>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="fields">
              {(provided: DroppableProvided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((field, index) => (
                    <Draggable key={field.key} draggableId={field.key} index={index}>
                      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                        <DraggableItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                        >
                          <CardContent>
                            <FieldEditor
                              field={field}
                              onUpdate={(updates) => updateField(index, updates)}
                              onDelete={() => deleteField(index)}
                            />
                          </CardContent>
                        </DraggableItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <StyledButton
            startIcon={<AddIcon />}
            onClick={addField}
            variant="contained"
            sx={{ mt: 2 }}
          >
            Add Field
          </StyledButton>
        </TemplatePanel>

        <SidePanel elevation={1}>
          <Typography variant="h6" gutterBottom>Template Preview</Typography>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(fields, null, 2)}
          </pre>
        </SidePanel>
      </EditorSection>

      <SaveFab
        onClick={handleSave}
        disabled={isSaving}
      >
        {isSaving ? <CircularProgress size={24} /> : <SaveIcon />}
      </SaveFab>

      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" onClose={() => setShowSaveSuccess(false)}>
          Changes saved successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveError}
        autoHideDuration={3000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      </Snackbar>
    </EditorContainer>
  );
};

export default TemplateEditor; 